import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../api/hooks/useAuth";
import { useToast } from "../../admin/context/ToastContext";
import checkoutService from "../../api/services/checkout.service";
import userService from "../../api/services/user.service";
import paymentService from "../../api/services/payment.service";
import orderService from "../../api/services/order.service";
import tokenManager from "../../api/utils/tokenManager";
import { loadScript } from "../../utils/scriptLoader";
import { normalizeImageUrl } from "../../utils/imageUtils";
import StateSelector from "../../components/StateSelector/StateSelector";
import "./Checkout.css";

const DEFAULT_COUNTRY_CODE = '+1';

// Every field that invalidates a selected address / its rates when edited
const ADDRESS_FIELDS = [
  "name", "email", "mobile", "apartment_number", "address", "city", "state", "pincode"
];

// apartment_number is optional
const REQUIRED_ADDRESS_FIELDS = ADDRESS_FIELDS.filter((f) => f !== "apartment_number");

const normalizePhoneValue = (mobile, countryCode = DEFAULT_COUNTRY_CODE) => {
  const rawMobile = String(mobile || '').trim();
  if (!rawMobile) return '';
  if (rawMobile.startsWith('+')) return rawMobile.replace(/[^\d+]/g, '');
  return `${countryCode}${rawMobile.replace(/\D/g, '')}`;
};

const getDurationLabel = (days) => {
  if (!days) return "";
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months} M`;
  }
  const weeks = Math.floor(days / 7);
  return `${weeks} W`;
};

export default function Checkout() {
  const { cartItems: contextCartItems, getCartTotal, upgradeGuestCartToUser, fetchCart, hasFetchedCart } = useCart();
  const { user: authUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { uuid: checkoutUuidFromUrl } = useParams();

  const [checkoutData, setCheckoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");

  const [selectedPaymentGatewayUuid, setSelectedPaymentGatewayUuid] = useState("");
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAddressUuid, setSelectedAddressUuid] = useState("");
  const [selectedCardUuid, setSelectedCardUuid] = useState(""); // Removed hardcoded dummy card
  const [isDeclarationChecked, setIsDeclarationChecked] = useState(false);
  const [savedFormAddressUuid, setSavedFormAddressUuid] = useState("");
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedRateId, setSelectedRateId] = useState("");
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [isSelectingRate, setIsSelectingRate] = useState(false);
  const [ratesError, setRatesError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    apartment_number: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  useEffect(() => {
    const initCheckout = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use authUser uuid directly; fallback to tokenManager if necessary
        const userUuid = authUser?.uuid || authUser?.id || tokenManager.getUserData('user')?.uuid;

        if (!userUuid) {
          throw new Error('User session expired. Please log in again.');
        }

        // Safety migration: ensure guest cart is linked before checkout is created/fetched.
        await upgradeGuestCartToUser({ user_uuid: userUuid }, { silent: true });

        let response;
        if (checkoutUuidFromUrl) {
          // If we have a UUID in the URL, fetch existing checkout details
          response = await checkoutService.getCheckout(checkoutUuidFromUrl, userUuid);
        } else {
          // Otherwise create a new checkout session (fallback)
          response = await checkoutService.createCheckout(userUuid);
        }

        if (response?.status || response?.success) {
          // If cart is empty, redirect to home
          const checkoutDetails = response.data?.details || response.data?.checkout_details || [];
          if (checkoutDetails.length === 0) {
            navigate('/');
            return;
          }
          
          setCheckoutData(response.data);
          if (authUser) {
            setFormData(prev => ({
              ...prev,
              name: authUser.name || "",
              email: authUser.email || "",
              mobile: authUser.mobile || authUser.phone || "",
            }));
          }
          await fetchSavedAddresses();
          await fetchPaymentMethods();
          await fetchSavedCards(); // New helper to fetch actual user cards
        } else {
          throw new Error(response?.message || 'Failed to initialize checkout');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchSavedAddresses = async () => {
      setIsLoadingAddresses(true);
      try {
        const result = await userService.listAddresses();
        if (result.status || result.success) {
          setSavedAddresses(result.data || []);
          // Auto-select removed: User must now manually select an address
        }
      } catch (err) {
      } finally {
        setIsLoadingAddresses(false);
      }
    };

    const fetchPaymentMethods = async () => {
      try {
        const result = await paymentService.listUserPayments();
        if (result.status || result.success) {
          const methods = result.data || [];
          setAvailablePaymentMethods(methods);
          if (methods.length > 0) {
            setPaymentMethod(methods[0].name.toLowerCase());
            setSelectedPaymentGatewayUuid(methods[0].uuid || methods[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch payment methods:", err);
      }
    };

    const fetchSavedCards = async () => {
      // Logic to fetch user cards from paymentService if available
      try {
        // If the service has a listCards method, we should call it
        if (paymentService.listUserCards) {
          const result = await paymentService.listUserCards();
          if (result.success && result.data?.length > 0) {
            setSelectedCardUuid(result.data[0].uuid);
          }
        }
      } catch (err) {
        console.error("Failed to fetch cards:", err);
      }
    };

    initCheckout();
  }, [authUser, checkoutUuidFromUrl]);

  // First check: if cart is empty, redirect to home.
  // Only after the cart has actually been fetched — on page refresh the context
  // starts as [] before the API responds, which used to wrongly kick users home.
  useEffect(() => {
    if (hasFetchedCart && contextCartItems && contextCartItems.length === 0) {
      navigate('/');
      return;
    }
  }, [hasFetchedCart, contextCartItems, navigate]);

  // Refresh checkout data when cart items change
  useEffect(() => {
    const refreshCheckout = async () => {
      if (!authUser || !contextCartItems || contextCartItems.length === 0) return;
      
      try {
        const userUuid = authUser?.uuid || authUser?.id || tokenManager.getUserData('user')?.uuid;
        
        // Create a new checkout session to sync with current cart
        const response = await checkoutService.createCheckout(userUuid);
        
        if (response?.status || response?.success) {
          const checkoutDetails = response.data?.details || response.data?.checkout_details || [];
          if (checkoutDetails.length === 0) {
            navigate('/');
            return;
          }
          setCheckoutData(response.data);
        }
      } catch (err) {
        console.error('Failed to refresh checkout after cart change:', err);
      }
    };

    refreshCheckout();
  }, [contextCartItems, authUser, navigate]);
  
  // Check if checkout has items, if not redirect to home
  useEffect(() => {
    if (!checkoutData) return;
    
    const checkoutDetails = checkoutData?.details || checkoutData?.checkout_details || [];
    if (checkoutDetails.length === 0) {
      navigate('/');
    }
  }, [checkoutData, navigate]);

  // Rates come back either raw from Shippo or already normalised, so read both shapes
  const normalizeRate = (rate, index) => {
    const service = rate?.servicelevel?.name || rate?.servicelevel_name || rate?.service || rate?.service_name || "";
    const days = rate?.estimated_days ?? rate?.days ?? rate?.delivery_days ?? null;
    // rate_id is what /checkout/select/rate expects - never fall back to a name here
    const rateId = rate?.rate_id || rate?.object_id || rate?.rate_uuid || rate?.uuid || rate?.id || "";
    return {
      id: String(rateId || `rate-${index}`),
      rateId: String(rateId || ""),
      amount: Number(rate?.amount ?? rate?.rate ?? rate?.price ?? 0),
      currency: rate?.currency || rate?.currency_local || "USD",
      provider: rate?.provider || rate?.carrier || rate?.carrier_account || "",
      service,
      days,
      terms: rate?.duration_terms || rate?.estimated_delivery || "",
      raw: rate,
    };
  };

  const fetchShippingRates = useCallback(async (addressUuid) => {
    const userUuid = authUser?.uuid || authUser?.id || tokenManager.getUserData('user')?.uuid;
    const checkoutUuid = checkoutData?.uuid || checkoutUuidFromUrl;

    if (!addressUuid || !checkoutUuid || !userUuid) return;

    setIsLoadingRates(true);
    setRatesError("");
    setShippingRates([]);
    setSelectedRateId("");

    try {
      const response = await checkoutService.getShippingRates(checkoutUuid, addressUuid, userUuid);
      const payload = response?.data ?? response;
      const list = Array.isArray(payload) ? payload : (payload?.rates || payload?.data || []);
      const rates = list.map(normalizeRate);

      if (!rates.length) {
        setRatesError("No delivery options available for this address.");
        return;
      }

      rates.sort((a, b) => a.amount - b.amount);
      setShippingRates(rates);
      // No auto-select: the backend only knows about a rate once the user picks one
    } catch (err) {
      console.error("[Checkout] Failed to load shipping rates:", err);
      setRatesError(err?.response?.data?.message || "Could not load delivery options. Please try again.");
    } finally {
      setIsLoadingRates(false);
    }
  }, [authUser, checkoutData, checkoutUuidFromUrl]);

  const clearShippingRates = () => {
    setShippingRates([]);
    setSelectedRateId("");
    setRatesError("");
  };

  const handleSelectRate = async (rate) => {
    if (isSelectingRate || selectedRateId === rate.id) return;

    const userUuid = authUser?.uuid || authUser?.id || tokenManager.getUserData('user')?.uuid;
    const checkoutUuid = checkoutData?.uuid || checkoutUuidFromUrl;

    if (!rate.rateId) {
      showToast("This delivery option can't be selected right now", "error");
      return;
    }

    const previousRateId = selectedRateId;
    setSelectedRateId(rate.id);
    setIsSelectingRate(true);

    try {
      const response = await checkoutService.selectRate(checkoutUuid, rate.rateId);
      if (!response?.status && !response?.success) {
        throw new Error(response?.message || "Failed to select delivery option");
      }

      // Shipping and total are recalculated server-side, so re-read them
      const refreshed = await checkoutService.getCheckout(checkoutUuid, userUuid);
      if (refreshed?.status || refreshed?.success) {
        setCheckoutData(refreshed.data);
      }
    } catch (err) {
      console.error("[Checkout] Failed to select rate:", err);
      setSelectedRateId(previousRateId);
      showToast(err?.response?.data?.message || err.message || "Failed to select delivery option", "error");
    } finally {
      setIsSelectingRate(false);
    }
  };

  const handleSelectAddress = (address) => {
    const isCurrentlySelected = selectedAddressUuid === (address.uuid || address.id);

    if (isCurrentlySelected) {
      // If clicking the same address again, just deselect it (don't wipe form)
      setSelectedAddressUuid("");
      clearShippingRates();
    } else {
      // Otherwise select the new address and update form
      const addressUuid = address.uuid || address.id;
      setSelectedAddressUuid(addressUuid);
      fetchShippingRates(addressUuid);
      setFormData({
        name: address.name || authUser?.name || "",
        email: address.email || authUser?.email || "",
        mobile: address.mobile || address.phone || "",
        apartment_number: address.apartment_number || "",
        address: address.address || address.street || "",
        city: address.city || "",
        state: address.state || "",
        pincode: address.pincode || address.zip || "",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If user modifies address fields, clear the selected address UUID
    // to trigger creation of a new address on order placement
    if (ADDRESS_FIELDS.includes(name)) {
      setSelectedAddressUuid("");
      // Rates belong to the previous address
      clearShippingRates();
    }
  };

  const buildAddressPayload = (userUuid) => ({
    user_uuid: userUuid,
    name: formData.name,
    email: formData.email,
    mobile: normalizePhoneValue(formData.mobile),
    apartment_number: formData.apartment_number,
    address: formData.address,
    city: formData.city,
    state: formData.state,
    pincode: String(formData.pincode || ""),
  });

  const isAddressFormComplete = REQUIRED_ADDRESS_FIELDS.every(
    (field) => String(formData[field] || "").trim()
  );

  // Form is hidden behind the saved list unless there is nothing to pick from
  const showAddressForm = savedAddresses.length === 0 || isAddingAddress;

  const openNewAddressForm = () => {
    setIsAddingAddress(true);
    setSelectedAddressUuid("");
    setSavedFormAddressUuid("");
    clearShippingRates();
    setFormData({
      name: authUser?.name || "",
      email: authUser?.email || "",
      mobile: "",
      apartment_number: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
    });
  };

  const cancelNewAddressForm = () => {
    setIsAddingAddress(false);
    setSavedFormAddressUuid("");
    setSelectedAddressUuid("");
    clearShippingRates();
  };

  // Manual address: save it first so we have a uuid to price rates against
  const handleSaveAddressForRates = async () => {
    if (isSavingAddress) return;

    const userUuid = authUser?.uuid || authUser?.id || tokenManager.getUserData('user')?.uuid;
    if (!userUuid) {
      showToast("Please sign in to continue", "error");
      return;
    }
    if (!isAddressFormComplete) {
      showToast("Please fill in all required shipping information", "error");
      return;
    }

    setIsSavingAddress(true);
    try {
      const payload = buildAddressPayload(userUuid);
      // Update the address we already created here rather than adding another
      const result = savedFormAddressUuid
        ? await userService.updateAddress(savedFormAddressUuid, payload)
        : await userService.createAddress(payload);

      if (!result.success) {
        throw new Error(result.message || "Failed to save shipping address");
      }

      const addressUuid = result.data?.uuid || result.data?.id || savedFormAddressUuid;
      if (!addressUuid) throw new Error("Address saved but no ID was returned");

      setSavedFormAddressUuid(addressUuid);
      setSelectedAddressUuid(addressUuid);

      const list = await userService.listAddresses();
      if (list.success) setSavedAddresses(list.data || []);

      // Collapse back to the saved list with the new address selected
      setIsAddingAddress(false);
      showToast("Address saved", "success");

      await fetchShippingRates(addressUuid);
    } catch (err) {
      console.error("[Checkout] Failed to save address for rates:", err);
      showToast(err.message || "Failed to save shipping address", "error");
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handlePlaceOrder = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isProcessing) return;

    if (!paymentMethod) {
      showToast("Please select a payment method", "error");
      return;
    }

    if (!isDeclarationChecked) {
      showToast("Please confirm the declaration by checking the box", "error");
      return;
    }

    // The address and rate reach the backend through /checkout/rates and
    // /checkout/select/rate, so both must be done before the order is created
    if (!selectedAddressUuid) {
      showToast("Please select a shipping address", "error");
      return;
    }

    if (!selectedRateId) {
      showToast("Please select a delivery option", "error");
      return;
    }

    const userUuid = authUser?.uuid || authUser?.id || tokenManager.getUserData()?.uuid || tokenManager.getUserData()?.id;

    setIsProcessing(true);

    try {
      const orderPayload = {
        user_uuid: userUuid,
        checkout_uuid: checkoutData?.uuid,
        payment_gateway_uuid: selectedPaymentGatewayUuid,
      };

      const response = await orderService.createOrder(orderPayload);

      if (response.status || response.success) {
        const orderData = response.data;

        // The response may be a single order, an array, or wrapped in .orders
        const orderList = Array.isArray(orderData)
          ? orderData
          : orderData?.orders || orderData?.data || [orderData];

        const orderUuuids = orderList
          .map((o) => (typeof o === "string" ? o : o?.order_uuid || o?.uuid || o?.id))
          .filter(Boolean);

        if (!orderUuuids.length) {
          throw new Error("Order was created but no order ID was returned");
        }


        // Step 2: Hit payment/create with the order UUIDs
        const paymentResponse = await orderService.createPayment({
          order_uuids: orderUuuids
        });

        if (!paymentResponse.status && !paymentResponse.success) {
          throw new Error(paymentResponse.message || "Failed to initiate payment session");
        }

        const gatewayData = paymentResponse.data;



        if (paymentMethod === 'stripe') {
          // Handle Stripe flow: Redirect to Stripe Checkout URL
          const checkoutUrl = gatewayData.checkout_url || gatewayData.gateway?.checkout_url || gatewayData.payment_url;
          if (checkoutUrl) {
            localStorage.setItem('pending_order_uuid', orderUuuids[0]);
            window.location.href = checkoutUrl;
          } else {
            throw new Error("Stripe checkout URL not found in response");
          }
        } else if (paymentMethod === 'paypal') {
          // Handle PayPal flow: Redirect to PayPal Checkout URL
          const checkoutUrl = gatewayData.checkout_url || gatewayData.payment_url;
          const paypalOrderId = gatewayData.order_id || gatewayData.paypal_order_id;
          if (checkoutUrl) {
            localStorage.setItem('pending_order_uuid', orderUuuids[0]);
            if (paypalOrderId) {
              localStorage.setItem('pending_paypal_order_id', paypalOrderId);
            }
            window.location.href = checkoutUrl;
          } else {
            throw new Error("PayPal checkout URL not found in response");
          }
        } else if (paymentMethod === 'razorpay') {
          // Handle Razorpay flow: Open Razorpay popup
          const scriptLoaded = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
          if (!scriptLoaded) {
            throw new Error("Razorpay SDK failed to load");
          }

          // Extract Razorpay specific IDs from response
          // Be robust: check paymentResponse.data, orderData.gateway (Step 1), and multiple field names
          // const razorpayId = gatewayData?.payment_gateway_id ||
          //   gatewayData?.gateway?.payment_gateway_id ||
          //   gatewayData?.id ||
          //   gatewayData?.razorpay_order_id ||
          //   orderData?.gateway?.payment_gateway_id;

          const razorpayId = gatewayData?.order_id;

          const gatewayType = gatewayData?.gateway;

          if (!razorpayId) {
            console.error("[Checkout] Razorpay ID missing. Gateway Data:", gatewayData, "Order Data:", orderData);
            throw new Error("Razorpay subscription/payment ID not found in response");
          }

          const options = {
            key: gatewayData?.razorpay_key_id ||
              gatewayData?.gateway?.razorpay_key_id ||
              orderData?.razorpay_key_id ||
              orderData?.gateway?.razorpay_key_id ||
              process.env.REACT_APP_RAZORPAY_KEY_ID,
            name: "Chill Tree",
            description: "Product Purchase",
            image: "/logo192.png",
            handler: function (razorpayResponse) {

              // Redirect to the success page with Razorpay parameters for verification
              // We do not verify here to avoid hitting the backend twice (it will be verified on the success page)
              const razorpayOrderId = razorpayResponse.razorpay_order_id || razorpayResponse.razorpay_subscription_id || razorpayId;

              const successUrl = `/success?order_uuid=${orderUuuids[0]}&razorpay_payment_id=${razorpayResponse.razorpay_payment_id}&razorpay_order_id=${razorpayOrderId}&razorpay_signature=${razorpayResponse.razorpay_signature}&payment_gateway_uuid=${selectedPaymentGatewayUuid}`;

              navigate(successUrl);
            },
            prefill: {
              name: formData.name,
              email: formData.email,
              contact: formData.mobile,
            },
            notes: {
              order_uuids: orderUuuids.join(','),
              transaction_uuid: gatewayData?.transaction_uuid || orderData?.transaction_uuid
            },
            theme: {
              color: "#00B8D9",
            },
            modal: {
              ondismiss: function () {
                setIsProcessing(false);
                showToast("Payment cancelled.", "info");
              }
            }
          };

          if (gatewayType === 'subscription') {
            options.subscription_id = razorpayId;
          } else {
            options.order_id = razorpayId;
          }

          const rzp = new window.Razorpay(options);

          // Store the primary order_uuid for verification fallback
          localStorage.setItem('pending_order_uuid', orderUuuids[0]);

          rzp.on('payment.failed', function (response) {
            showToast(`Payment failed: ${response.error.description}`, "error");
          });
          rzp.open();
        } else {
          showToast("Order placed successfully!", "success");
          // Refresh cart since it might be cleared by backend on non-gateway order creation
          fetchCart().catch(err => console.error("Failed to refresh cart after order creation:", err));
          navigate("/profile");
        }
      } else {
        throw new Error(response.message || "Failed to place order");
      }
    } catch (err) {
      console.error("Order placement error:", err);
      showToast(err.message || "An error occurred while placing your order", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  // Use only backend create response
  const checkoutItems = checkoutData?.details || [];
  const subtotal = checkoutData?.item_total ?? checkoutData?.amount ?? 0;
  // Backend recalculates shipping when the rate is selected, so trust the checkout
  const shipping = checkoutData?.shipping_price;

  // Calculate tax from individual items if order-level tax is 0
  const orderLevelTax = checkoutData?.tax ?? 0;
  const itemsTax = checkoutItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const tax = orderLevelTax > 0 ? orderLevelTax : itemsTax;
  
  const displayTotal = checkoutData?.total_amount ?? checkoutData?.amount ?? 0;
  const discount = checkoutData?.discount_amount ?? 0;
  const isSubscription = checkoutData?.is_subscription_enabled || checkoutItems.some(item => item.is_subscription_enabled);


  if (loading) {
    return (
      <div className="checkout-page-container">
        <div className="checkout-header">
          <h1 className="checkout-title">Checkout</h1>
          <div className="checkout-accent-line"></div>
        </div>
        <div className="checkout-loading">
          <div className="loading-spinner"></div>
          <p>Preparing your checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-page-container">
        <div className="checkout-header">
          <h1 className="checkout-title">Checkout</h1>
          <div className="checkout-accent-line"></div>
        </div>
        <div className="checkout-error">
          <div className="error-icon">⚠️</div>
          <h2 className="error-title">Unable to load checkout</h2>
          <p className="error-message">{error}</p>
          <Link to="/cart" className="continue-shopping-btn">
            Back to Cart
          </Link>
        </div>
      </div>
    );
  }

  if (checkoutItems.length === 0) {
    return (
      <div className="checkout-page-container">
        <div className="checkout-header">
          <h1 className="checkout-title">Checkout</h1>
          <div className="checkout-accent-line"></div>
        </div>

        <div className="empty-checkout">
          <div className="empty-checkout-icon">🛒</div>
          <h2 className="empty-checkout-title">Your cart is empty</h2>
          <p className="empty-checkout-text">Add items to your cart to proceed with checkout.</p>
          <Link to="/" className="continue-shopping-btn">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page-container">
      <div className="checkout-page-header">
        <h1 className="checkout-page-title">Checkout</h1>
        <div className="checkout-title-underline"></div>
      </div>

      <div className="trust-bar-container">
        <div className="trust-bar-card">
          <div className="trust-bar-item">
            <span className="trust-bar-icon">🔒</span>
            <span className="trust-bar-text">Secure checkout</span>
          </div>
          <div className="trust-bar-item">
            <span className="trust-bar-icon">🚚</span>
            <span className="trust-bar-text">Fast delivery</span>
          </div>
          <div className="trust-bar-item">
            <span className="trust-bar-icon">💳</span>
            <span className="trust-bar-text">Safe payments</span>
          </div>
        </div>
      </div>

      <div className="checkout-main-layout">
        <div className="checkout-left-column">
          {/* Step 1 - Shipping address */}
          <div className="shipping-form-card">
            <div className="step-head">
              <span className="step-badge">1</span>
              <div className="step-heading">
                <h2 className="form-title">Shipping Address</h2>
                <p className="step-subtitle">Where should we deliver your order?</p>
              </div>
              {savedAddresses.length > 0 && !isAddingAddress && (
                <button type="button" className="add-address-btn" onClick={openNewAddressForm}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add new address
                </button>
              )}
            </div>

            {/* Saved addresses - the form stays hidden while these exist */}
            {savedAddresses.length > 0 && !isAddingAddress && (
              <div className="saved-addresses-selector">
                <div
                  className={`address-options-grid${savedAddresses.length > 4 ? " is-scrollable" : ""}`}
                  role="radiogroup"
                  aria-label="Saved addresses"
                >
                  {savedAddresses.map((address) => {
                    const addressId = address.uuid || address.id;
                    const isSelected = selectedAddressUuid === addressId;
                    return (
                      <div
                        key={addressId}
                        role="radio"
                        tabIndex={0}
                        aria-checked={isSelected}
                        className={`address-option-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectAddress(address)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectAddress(address);
                          }
                        }}
                      >
                        {isSelected && (
                          <div className="selected-tick">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        )}
                        <div className="option-header">
                          <span className="option-name">{address.name}</span>
                          {address.isDefault && <span className="default-tag">Default</span>}
                        </div>
                        <div className="option-details">
                          {address.apartment_number && <span>{address.apartment_number}, </span>}
                          {address.address || address.street}
                        </div>
                        <div className="option-details option-region">
                          {[address.city, address.state, address.pincode || address.zip]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {showAddressForm && (
              <div className="new-address-panel">
                {savedAddresses.length > 0 && (
                  <div className="panel-head">
                    <h3 className="panel-title">New address</h3>
                    <button type="button" className="panel-cancel" onClick={cancelNewAddressForm}>
                      Cancel
                    </button>
                  </div>
                )}

                <form className="checkout-form">
                <div className="form-group full-width">
                  <label htmlFor="name" className="form-label">Full Name <span style={{color: 'red'}}>*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="email" className="form-label">Email Address <span style={{color: 'red'}}>*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-row two-columns">
                  <div className="form-group">
                    <label htmlFor="mobile" className="form-label">Phone <span style={{color: 'red'}}>*</span></label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="apartment_number" className="form-label">
                      Door/Apt Number <span className="form-optional">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="apartment_number"
                      name="apartment_number"
                      value={formData.apartment_number}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Apt 4B"
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="address" className="form-label">Street Address <span style={{color: 'red'}}>*</span></label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="form-row two-columns">
                  <div className="form-group">
                    <label htmlFor="city" className="form-label">City <span style={{color: 'red'}}>*</span></label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      placeholder="New York"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state" className="form-label">State <span style={{color: 'red'}}>*</span></label>
                    <StateSelector
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="pincode" className="form-label">Zipcode <span style={{color: 'red'}}>*</span></label>
                  <input
                    type="text"
                    id="pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="567567"
                  />
                </div>

                  <div className="form-group full-width save-address-row">
                    <button
                      type="button"
                      className="save-address-btn"
                      onClick={handleSaveAddressForRates}
                      disabled={!isAddressFormComplete || isSavingAddress || isLoadingRates}
                    >
                      {isSavingAddress ? "Saving address..." : "Use this address"}
                    </button>
                    <p className="save-address-hint">
                      {isAddressFormComplete
                        ? "We'll save this address and show delivery options for it."
                        : "Fill in all required fields to continue."}
                    </p>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Step 2 - Delivery options for the chosen address */}
          <div className={`delivery-step-card${selectedAddressUuid ? "" : " is-pending"}`}>
            <div className="step-head">
              <span className="step-badge">2</span>
              <div className="step-heading">
                <h2 className="form-title">Delivery Options</h2>
                <p className="step-subtitle">
                  {selectedAddressUuid
                    ? "Choose how fast you want it delivered."
                    : "Select a shipping address first."}
                </p>
              </div>
            </div>

            {selectedAddressUuid && (
              <div className="delivery-rates" aria-live="polite">
                {isLoadingRates ? (
                  <div className="rates-skeleton" aria-label="Loading delivery options">
                    <span className="skeleton-row" />
                    <span className="skeleton-row" />
                  </div>
                ) : ratesError ? (
                  <div className="rates-status rates-error" role="alert">
                    <span>{ratesError}</span>
                    <button
                      type="button"
                      className="rates-retry"
                      onClick={() => fetchShippingRates(selectedAddressUuid)}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <div className="rates-list" role="radiogroup" aria-label="Delivery options">
                    {shippingRates.map((rate, index) => (
                      <button
                        type="button"
                        key={rate.id}
                        role="radio"
                        className={`rate-option${selectedRateId === rate.id ? " selected" : ""}`}
                        onClick={() => handleSelectRate(rate)}
                        disabled={isSelectingRate}
                        aria-checked={selectedRateId === rate.id}
                      >
                        <span className="rate-radio" aria-hidden="true" />
                        <span className="rate-info">
                          <span className="rate-name">
                            {rate.service || "Shipping"}
                            {index === 0 && shippingRates.length > 1 && (
                              <span className="rate-tag">Cheapest</span>
                            )}
                          </span>
                          <span className="rate-meta">
                            {rate.provider && <span className="rate-provider">{rate.provider}</span>}
                            {rate.days
                              ? `Arrives in ${rate.days} business day${rate.days > 1 ? "s" : ""}`
                              : rate.terms || "Delivery time unavailable"}
                          </span>
                        </span>
                        <span className="rate-price">
                          {isSelectingRate && selectedRateId === rate.id
                            ? "..."
                            : rate.amount === 0
                              ? "Free"
                              : `$${rate.amount.toFixed(2)}`}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Method Selection */}
          <div className="payment-method-card">
            <div className="step-head">
              <span className="step-badge">3</span>
              <div className="step-heading">
                <h2 className="form-title">Payment Method</h2>
                <p className="step-subtitle">All transactions are encrypted and secure.</p>
              </div>
            </div>
            <div className="payment-options">
              {availablePaymentMethods.length > 0 ? (
                availablePaymentMethods.map((method) => (
                  <div
                    key={method.uuid || method.id}
                    className={`payment-option-card ${paymentMethod === method.name.toLowerCase() ? 'selected' : ''}`}
                    onClick={() => {
                      setPaymentMethod(method.name.toLowerCase());
                      setSelectedPaymentGatewayUuid(method.uuid || method.id);
                    }}
                  >
                    <div className="payment-radio">
                      <div className="radio-circle"></div>
                    </div>
                    <div className="payment-info">
                      <span className="payment-name">{method.name}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-payment-methods">No payment methods available</div>
              )}
            </div>
          </div>

          {/* Checkout Specific Footer (Image 3) */}
          <div className="checkout-footer-section">
            {/* Declaration Checkbox */}
            <div className="declaration-container">
              <label className="declaration-checkbox-label">
                <input
                  type="checkbox"
                  checked={isDeclarationChecked}
                  onChange={(e) => setIsDeclarationChecked(e.target.checked)}
                  className="declaration-checkbox"
                />
                <span className="declaration-text">
                  I confirm that I am of legal age to purchase and consume these products. I understand that my order may be subject to age verification and that if verification fails, my order may be cancelled.
                </span>
              </label>
            </div>

            <button
              type="button"
              onClick={handlePlaceOrder}
              className={`pay-now-btn ${isProcessing ? 'processing' : ''} ${!isDeclarationChecked ? 'disabled' : ''}`}
              disabled={isProcessing || !isDeclarationChecked}
            >
              {isProcessing ? (
                <>
                  <span className="spinner-small"></span>
                  Processing...
                </>
              ) : "Pay now"}
            </button>

            <p className="checkout-disclaimer">
              One or more items in your cart is a deferred, subscription, or recurring purchase.
              By continuing with your payment, you agree that your payment method will
              automatically be charged at the price and frequency listed on this page until it
              ends or you cancel. All cancellations are subject to the <Link to="/cancellation-policy">cancellation policy</Link>.
            </p>

            <div className="checkout-policy-links">
              <Link to="/refund-policy">Refund policy</Link>
              <Link to="/privacy-policy">Privacy policy</Link>
              <Link to="/terms-of-service">Terms of service</Link>
              <Link to="/cancellation-policy">Cancellations</Link>
            </div>
          </div>
        </div>

        {/* Order Summary Column (Right Side - Image 2) */}
        <div className="checkout-right-column">
          <div className="order-summary-card">
            <div className="summary-card-head">
              <h2 className="summary-card-title">Order Summary</h2>
              <span className="summary-count-badge">
                {checkoutItems.length} {checkoutItems.length === 1 ? "item" : "items"}
              </span>
            </div>

            <div className={`summary-items-list${checkoutItems.length > 2 ? " is-scrollable" : ""}`}>
              {checkoutItems.map((item, index) => {
                const rawImage = item.product_image || item.image || item.image_url;
                const imageToNormalize = Array.isArray(rawImage) ? rawImage[0] : rawImage;
                const imageUrl = normalizeImageUrl(imageToNormalize) || "/product-placeholder.png";
                
                return (
                  <div key={item.uuid || item.id || index} className="summary-item">
                    <div className="summary-item-image">
                      <img src={imageUrl} alt={item.product_name || item.name} />
                    </div>
                    <div className="summary-item-content">
                      <div className="summary-item-header">
                        <h3 className="summary-item-name">{item.product_name || item.name}</h3>
                      </div>
                      
                      <div className="summary-item-details">
                        {(() => {
                          const basePrice = parseFloat(item.variant_price ?? item.price ?? 0);
                          const unitPrice = parseFloat(item.unit_price ?? basePrice);
                          const discountPercentage = parseFloat(item.discount_percentage ?? 0);
                          const quantity = parseFloat(item.quantity ?? 1);
                          const durationLabel = getDurationLabel(item.billing_duration);
                          const effectiveSingleItemPrice = discountPercentage > 0 ? unitPrice : basePrice;
                          const isOneTime = item.purchase_type === 'one_time' || !item.is_subscription_enabled || item.billing_duration === 1;
                          
                          return (
                            <>
                              <div className="summary-item-detail-row">
                                <span className="summary-item-detail-label">Price:</span>
                                <div className="summary-item-detail-value">
                                  {discountPercentage > 0 && (
                                    <span className="summary-item-original-price">
                                      ${basePrice.toFixed(2)}
                                    </span>
                                  )}
                                  <span className="summary-item-single-price">
                                    ${effectiveSingleItemPrice.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="summary-item-detail-row">
                                <span className="summary-item-detail-label">Qty:</span>
                                <div className="summary-item-detail-value">
                                  <span className="summary-item-marker">× {quantity}</span>
                                </div>
                              </div>
                              
                              {!isOneTime ? (
                                <div className="summary-item-detail-row">
                                  <span className="summary-item-detail-label">Subscription:</span>
                                  <div className="summary-item-detail-value">
                                    <span className="summary-item-marker">× {durationLabel}</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="summary-item-detail-row">
                                  <span className="summary-item-detail-label">Purchase:</span>
                                  <div className="summary-item-detail-value">
                                    <span className="summary-item-type-one-time">One-time purchase</span>
                                  </div>
                                </div>
                              )}
                              
                              <div className="summary-item-total">
                                <span className="summary-item-total-label">Total price:</span>
                                <span className="summary-item-total-price">
                                  ${parseFloat(item.amount || item.total_amount || item.price).toFixed(2)}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="summary-calculations">
              <div className="calc-row">
                <span>Subtotal</span>
                <span>${parseFloat(subtotal).toFixed(2)}</span>
              </div>

              <div className="calc-row">
                <span>Shipping</span>
                <span className={!formData.address && (shipping == null) ? "enter-address-text" : ""}>
                  {shipping == null
                    ? (!formData.address ? "Enter shipping address" : "$0.00")
                    : `$${parseFloat(shipping).toFixed(2)}`}
                </span>
              </div>

              <div className="calc-row">
                  <span>Tax</span>
                  <span>${parseFloat(tax).toFixed(2)}</span>
                </div>

              {discount > 0 && (
                <div className="calc-row discount-row">
                  <span>Discount</span>
                  <span>-${parseFloat(discount).toFixed(2)}</span>
                </div>
              )}

              <div className="calc-row total-row">
                <div className="total-label-container">
                  <span className="total-label">Total</span>
                </div>
                <div className="total-value-container">
                  <span className="currency-label">USD</span>
                  <span className="total-value">${parseFloat(displayTotal).toFixed(2)}</span>
                </div>
              </div>

              {isSubscription && (
                <div className="recurring-subtotal">
                  <div className="recurring-info">
                    <div className="recurring-label">Recurring subtotal</div>
                    <div className="recurring-note">
                      Billing excl. shipping & taxes
                    </div>
                  </div>
                  <div className="recurring-value">
                    ${parseFloat(subtotal).toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



